import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { createIdentityFile } from '../moduleServices/identity_services';

const ALLOWED_EXT = ['.md', '.json', '.txt'];

const CreateFileModal = ({ isOpen, onClose, folderPrefix = '', onCreated }) => {
  const [path, setPath] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prefix = folderPrefix ? `${folderPrefix.replace(/\/$/, '')}/` : '';
    setPath(`${prefix}new-file.md`);
    setContent('');
  }, [isOpen, folderPrefix]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = path.trim().replace(/^\/+/, '');
    if (!clean) {
      toast.error('Path is required');
      return;
    }
    const ext = clean.includes('.') ? `.${clean.split('.').pop().toLowerCase()}` : '';
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Extension must be one of: ${ALLOWED_EXT.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createIdentityFile(clean, content);
      toast.success('File created');
      onCreated?.(result);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create file';
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">New identity file</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Path</label>
          <input
            autoFocus
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="self/SOUL.md"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-module-identity font-mono"
          />
          <p className="mt-1 text-xs text-gray-400">Allowed: .md, .json, .txt · max 1 MB</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Content (optional)</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            placeholder="# Title"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-module-identity font-mono resize-y"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-module-identity text-white rounded-lg text-sm font-medium hover:bg-module-identity/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateFileModal;
